import { useEffect, useState } from "react";
import { Loader2, UserPlus, Trash2, ShieldCheck, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from '@/lib/auth';

export default function CollaboratorsAdmin() {
  const { user } = useAuth();
  const [credentials, setCredentials] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ email: "", name: "" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/collaborators");
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/collaborators", { email: form.email.trim().toLowerCase(), name: form.name.trim() });
      setCredentials(res.data.temporary_password ? {email:res.data.email,password:res.data.temporary_password} : null);
      toast.success("Colaborador agregado", {
        description: `${form.email} ahora puede acceder con su correo y contraseña.`,
      });
      setForm({ email: "", name: "" });
      load();
    } catch (err) {
      toast.error("No se pudo agregar", {
        description: err?.response?.data?.detail || "Verifica que sea un correo válido.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`¿Eliminar a ${email}? Ya no podrá acceder al panel.`)) return;
    try {
      await api.delete(`/collaborators/${encodeURIComponent(email)}`);
      toast.success("Colaborador eliminado");
      load();
    } catch (err) {
      toast.error("Error al eliminar", { description: err?.response?.data?.detail || "" });
    }
  };

  const inputCls = "w-full border border-slate-300 rounded-none px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white";
  const labelCls = "block text-xs font-display font-bold text-slate-700 mb-1.5 uppercase tracking-wider";

  return (
    <div className="space-y-6">
      {credentials && <div role="status" className="border-2 border-amber-500 bg-amber-50 p-6 space-y-2">
        <p className="font-bold">Cuenta creada. Guarda y entrega estas credenciales al colaborador.</p>
        <p>{credentials.email}</p><code className="block break-all select-all">{credentials.password}</code>
        <p className="text-sm">Puede cambiar la contraseña en Mi cuenta. No se envió un correo automático.</p>
        <button onClick={()=>setCredentials(null)} className="underline text-sm">Ya las guardé</button>
      </div>}
      {/* Add form */}
      <div className="bg-white border-2 border-slate-950">
        <div className="border-b-2 border-slate-950 p-4 md:p-6">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600">Equipo</div>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Agregar colaborador</h2>
          <p className="text-sm text-slate-500 mt-1">
            El colaborador podrá editar productos, ver cotizaciones y mensajes. Recibirá una contraseña temporal al crear su cuenta.
          </p>
        </div>
        <form onSubmit={submit} className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-end" data-testid="collab-form">
          <div className="md:col-span-5">
            <label className={labelCls}>Correo electrónico *</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="nombre@gmail.com"
              className={inputCls}
              data-testid="collab-email-input"
            />
          </div>
          <div className="md:col-span-5">
            <label className={labelCls}>Nombre (opcional)</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Juan Pérez"
              className={inputCls}
              data-testid="collab-name-input"
            />
          </div>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="collab-add-btn"
              className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-black uppercase tracking-wider px-5 py-2.5 text-xs btn-brutal inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Agregar
            </button>
          </div>
        </form>
      </div>

      {/* List */}
      <div className="bg-white border-2 border-slate-950">
        <div className="border-b-2 border-slate-950 p-4 md:p-6">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-600">Acceso al panel</div>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Cuentas autorizadas</h2>
        </div>

        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {/* Master admin (env) row */}
            <li className="p-4 flex flex-wrap items-center gap-3" data-testid="master-row">
              <div className="w-9 h-9 bg-amber-500 border-2 border-slate-950 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-slate-950" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold uppercase text-sm tracking-tight">
                  Administrador master
                </div>
                <div className="font-mono text-xs text-slate-500 break-all">
                  {user.email}
                </div>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1">
                No editable
              </span>
            </li>

            {items.length === 0 ? (
              <li className="p-8 text-center text-slate-500 text-sm">
                <Mail className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                Sin colaboradores agregados. Agrega cuentas para compartir la administración.
              </li>
            ) : items.map((c) => (
              <li key={c.email} data-testid={`collab-${c.email}`} className="p-4 flex flex-wrap items-center gap-3">
                <div className="w-9 h-9 bg-slate-100 border border-slate-300 flex items-center justify-center font-display font-black text-slate-950">
                  {(c.name || c.email)[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm">
                    {c.name || <span className="text-slate-400 italic font-mono text-xs">Sin nombre</span>}
                  </div>
                  <div className="font-mono text-xs text-slate-600 break-all">{c.email}</div>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400 whitespace-nowrap">
                  Agregado {new Date(c.added_at).toLocaleDateString("es-CL")}
                </span>
                <button
                  onClick={() => remove(c.email)}
                  data-testid={`collab-delete-${c.email}`}
                  className="p-2 hover:bg-red-50 text-red-600"
                  title="Eliminar colaborador"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
